import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Check, GripVertical, Target, Type } from 'lucide-react';

// ===== Release The Tension - Drag & Release Exercise =====
export const ReleaseTensionExercise = ({ onComplete, onExit }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [released, setReleased] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const containerRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrag = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const container = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setPosition({
      x: clientX - container.left - 75,
      y: clientY - container.top - 40
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging || !dropZoneRef.current || !containerRef.current) return;
    
    const dropZone = dropZoneRef.current.getBoundingClientRect();
    const container = containerRef.current.getBoundingClientRect();
    
    const itemCenterX = container.left + position.x + 75;
    const itemCenterY = container.top + position.y + 40;
    
    // Check if dropped in the release zone
    if (
      itemCenterX >= dropZone.left &&
      itemCenterX <= dropZone.right &&
      itemCenterY >= dropZone.top &&
      itemCenterY <= dropZone.bottom
    ) {
      setFadeOut(true);
      setTimeout(() => {
        setReleased(true);
      }, 800);
    }
    
    setIsDragging(false);
  }, [isDragging, position]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDrag);
      window.addEventListener('touchend', handleDragEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDrag);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDrag, handleDragEnd]);

  if (released) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6 animate-pulse">
          <Check className="w-10 h-10 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">You released it</h3>
        <p className="text-muted-foreground text-center mb-8">
          Take a deep breath. That weight is no longer yours to carry.
        </p>
        <button
          onClick={onComplete}
          className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:opacity-90 transition-opacity"
        >
          Complete
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="relative h-[400px] select-none"
      onMouseMove={handleDrag}
      onTouchMove={handleDrag}
    >
      {/* Instructions */}
      <div className="text-center mb-4">
        <p className="text-muted-foreground text-sm">Drag what feels heavy into the release zone</p>
      </div>

      {/* Draggable Weight */}
      <div
        className={`absolute cursor-grab active:cursor-grabbing transition-all duration-300 ${fadeOut ? 'opacity-0 scale-50' : 'opacity-100'}`}
        style={{
          left: position.x || '50%',
          top: position.y || '30%',
          transform: position.x ? 'none' : 'translateX(-50%)',
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className={`flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl border-2 ${isDragging ? 'border-primary shadow-lg shadow-primary/20' : 'border-border'} transition-all`}>
          <GripVertical className="w-5 h-5 text-muted-foreground" />
          <span className="text-foreground font-medium">What feels heavy right now</span>
        </div>
        <div className="text-center mt-2">
          <span className="text-xs text-muted-foreground">👆 Drag me</span>
        </div>
      </div>

      {/* Release Zone */}
      <div
        ref={dropZoneRef}
        className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 w-48 h-24 rounded-2xl border-2 border-dashed flex items-center justify-center transition-all duration-300 ${
          isDragging 
            ? 'border-cyan-400 bg-cyan-500/10 scale-110' 
            : 'border-muted bg-muted/20'
        }`}
      >
        <div className="text-center">
          <div className="text-3xl mb-1">✨</div>
          <span className={`text-sm ${isDragging ? 'text-cyan-400' : 'text-muted-foreground'}`}>
            Release Zone
          </span>
        </div>
      </div>

      {/* Exit Button */}
      <button
        onClick={onExit}
        className="absolute top-0 right-0 p-2 text-muted-foreground hover:text-foreground"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};


// ===== Focus - Cursor Tracking Exercise =====
export const FocusExercise = ({ targetDuration = 20, onComplete, onExit }) => {
  const [progress, setProgress] = useState(0);
  const [isInside, setIsInside] = useState(false);
  const [completed, setCompleted] = useState(false);
  const circleRef = useRef(null);
  const progressInterval = useRef(null);

  const checkIfInside = useCallback((e) => {
    if (!circleRef.current) return;
    
    const circle = circleRef.current.getBoundingClientRect();
    const centerX = circle.left + circle.width / 2;
    const centerY = circle.top + circle.height / 2;
    const radius = circle.width / 2;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const distance = Math.sqrt(
      Math.pow(clientX - centerX, 2) + Math.pow(clientY - centerY, 2)
    );
    
    setIsInside(distance <= radius);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', checkIfInside);
    window.addEventListener('touchmove', checkIfInside);
    
    return () => {
      window.removeEventListener('mousemove', checkIfInside);
      window.removeEventListener('touchmove', checkIfInside);
    };
  }, [checkIfInside]);

  useEffect(() => {
    if (isInside && !completed) {
      progressInterval.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + (100 / (targetDuration * 10));
          if (newProgress >= 100) {
            clearInterval(progressInterval.current);
            setCompleted(true);
            return 100;
          }
          return newProgress;
        });
      }, 100);
    } else {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      // Gentle reset - decay slowly instead of immediate reset
      if (!completed && progress > 0) {
        const decayInterval = setInterval(() => {
          setProgress(prev => {
            if (prev <= 0 || isInside) {
              clearInterval(decayInterval);
              return Math.max(0, prev);
            }
            return prev - 2;
          });
        }, 100);
      }
    }
    
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isInside, completed, targetDuration]);

  if (completed) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">Well done!</h3>
        <p className="text-muted-foreground text-center mb-8">
          You maintained focus for {targetDuration} seconds. Notice the calm you created.
        </p>
        <button
          onClick={onComplete}
          className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:opacity-90 transition-opacity"
        >
          Complete
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center py-8 px-6">
      {/* Instructions */}
      <div className="text-center mb-8">
        <p className="text-foreground font-medium mb-1">Keep your cursor inside the circle</p>
        <p className="text-muted-foreground text-sm">Hold for {targetDuration} seconds</p>
      </div>

      {/* Focus Circle */}
      <div className="relative">
        {/* Outer glow */}
        <div 
          className={`absolute inset-0 rounded-full transition-all duration-500 ${
            isInside 
              ? 'bg-cyan-500/30 blur-xl scale-125' 
              : 'bg-muted/20 blur-lg'
          }`}
        />
        
        {/* Main circle */}
        <div
          ref={circleRef}
          className={`relative w-48 h-48 rounded-full border-4 flex items-center justify-center transition-all duration-300 ${
            isInside 
              ? 'border-cyan-400 bg-cyan-500/10' 
              : 'border-muted bg-muted/10'
          }`}
        >
          {/* Progress ring */}
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="96"
              cy="96"
              r="90"
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              className="text-cyan-500/30"
            />
            <circle
              cx="96"
              cy="96"
              r="90"
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              strokeDasharray={565}
              strokeDashoffset={565 - (565 * progress) / 100}
              className="text-cyan-400 transition-all duration-100"
              strokeLinecap="round"
            />
          </svg>
          
          {/* Center content */}
          <div className="text-center z-10">
            <Target className={`w-10 h-10 mx-auto mb-2 ${isInside ? 'text-cyan-400' : 'text-muted-foreground'}`} />
            <span className={`text-2xl font-bold ${isInside ? 'text-cyan-400' : 'text-muted-foreground'}`}>
              {Math.round(progress)}%
            </span>
          </div>
        </div>
      </div>

      {/* Status */}
      <p className={`mt-6 text-sm transition-colors ${isInside ? 'text-cyan-400' : 'text-muted-foreground'}`}>
        {isInside ? 'Focused... keep going' : 'Move cursor inside the circle'}
      </p>

      {/* Exit Button */}
      <button
        onClick={onExit}
        className="mt-8 px-6 py-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        Exit
      </button>
    </div>
  );
};


// ===== Vent It Out - Fading Text Exercise =====
export const VentItOutExercise = ({ onComplete, onExit }) => {
  const [text, setText] = useState('');
  const [displayText, setDisplayText] = useState([]);
  const [totalChars, setTotalChars] = useState(0);
  const inputRef = useRef(null);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    const addedChars = newText.length - text.length;
    
    if (addedChars > 0) {
      const newChars = newText.slice(-addedChars).split('').map((char, idx) => ({
        id: Date.now() + idx,
        char,
        opacity: 1
      }));
      
      setDisplayText(prev => [...prev, ...newChars]);
      setTotalChars(prev => prev + addedChars);
    }
    
    setText(newText);
  };

  // Fade out characters over time
  useEffect(() => {
    const fadeInterval = setInterval(() => {
      setDisplayText(prev => 
        prev
          .map(item => ({
            ...item,
            opacity: Math.max(0, item.opacity - 0.02)
          }))
          .filter(item => item.opacity > 0)
      );
    }, 50);
    
    return () => clearInterval(fadeInterval);
  }, []);

  const handleComplete = () => {
    setText('');
    setDisplayText([]);
    onComplete();
  };

  return (
    <div className="flex flex-col py-6 px-6">
      {/* Header */}
      <div className="text-center mb-6">
        <Type className="w-8 h-8 text-purple-400 mx-auto mb-2" />
        <h3 className="text-lg font-bold text-foreground mb-1">Vent It Out</h3>
        <p className="text-muted-foreground text-sm">
          Type whatever is on your mind. Watch it disappear. Nothing is saved.
        </p>
      </div>

      {/* Display Area */}
      <div className="relative h-48 bg-muted/20 rounded-xl border border-border overflow-hidden mb-4">
        <div className="absolute inset-0 p-4 overflow-hidden">
          <div className="flex flex-wrap gap-0.5">
            {displayText.map(item => (
              <span
                key={item.id}
                className="text-foreground text-lg transition-opacity duration-100"
                style={{ opacity: item.opacity }}
              >
                {item.char === ' ' ? '\u00A0' : item.char}
              </span>
            ))}
          </div>
        </div>
        
        {/* Privacy indicator */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded-full">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Private - Never saved
        </div>
      </div>

      {/* Input */}
      <textarea
        ref={inputRef}
        value={text}
        onChange={handleTextChange}
        placeholder="Start typing... let it all out..."
        className="w-full h-24 p-4 bg-input border border-border rounded-xl text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50"
        autoFocus
      />

      {/* Stats */}
      <div className="flex items-center justify-between mt-4">
        <span className="text-xs text-muted-foreground">
          {totalChars} characters released
        </span>
        <div className="flex gap-3">
          <button
            onClick={onExit}
            className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            Exit
          </button>
          {totalChars > 10 && (
            <button
              onClick={handleComplete}
              className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-opacity"
            >
              I feel lighter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// ===== Interactive Exercise Wrapper =====
export const InteractiveExercise = ({ template, onComplete, onExit }) => {
  const interactiveType = template?.interactive_type;
  
  switch (interactiveType) {
    case 'drag_release':
      return <ReleaseTensionExercise onComplete={onComplete} onExit={onExit} />;
    case 'cursor_focus':
      return <FocusExercise targetDuration={template?.target_duration || 20} onComplete={onComplete} onExit={onExit} />;
    case 'fading_text':
      return <VentItOutExercise onComplete={onComplete} onExit={onExit} />;
    default:
      return null;
  }
};

export default InteractiveExercise;
